import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowRight, Smartphone, Download } from 'lucide-react';
import appVideo from '../../assets/shippers/appscreen.mp4';

const IntroSection = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [videoError, setVideoError] = useState(false);
    const sectionRef = useRef(null);
    const videoRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    // Start playing video when section becomes visible
                    if (videoRef.current && videoLoaded) {
                        videoRef.current.play().catch(console.error);
                    }
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, [videoLoaded]);

    const handleVideoLoad = useCallback(() => {
        setVideoLoaded(true);
    }, []);

    const handleVideoError = useCallback(() => {
        setVideoError(true);
        console.error('Failed to load mobile app video');
    }, []);

    return (
        <section
            ref={sectionRef}
            className="w-full bg-gray-50 pt-20 overflow-hidden"
            aria-labelledby="mobile-app-section-title"
            role="region"
        >
            {/* Top Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
                <div className="text-center">
                    <div
                        className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                            }`}
                    >
                        <h2
                            id="mobile-app-section-title"
                            className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 leading-tight mb-6"
                        >
                            Logistics Made Simple – Cost-Effective Solutions at{' '}
                            <span className="text-[#F05A28]">Your Fingertips!</span>
                        </h2>
                    </div>

                    <div
                        className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                            }`}
                        style={{ transitionDelay: '0.2s' }}
                    >
                        <p className="text-lg text-gray-600 leading-relaxed max-w-4xl mx-auto">
                            <span className="text-[#F05A28] font-semibold">RoadExpress</span> provides you the perfect yet cost-efficient logistics solution for your need without the hassle involved. Our transparent pricing structure and hassle-free freight management helps you choose the perfect solution for your logistical need. Get the perfect logistical solution integrated with technology, manage all your shipments and all related information on a single platform with real-time vehicle tracking and POD status.
                        </p>
                    </div>
                </div>
            </div>

            {/* Enhanced SEO Structured Data */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "MobileApplication",
                        "name": "RoadExpress Mobile App",
                        "description": "Cost-effective logistics solution mobile app with real-time tracking and transparent pricing",
                        "applicationCategory": "BusinessApplication",
                        "operatingSystem": "iOS, Android",
                        "offers": {
                            "@type": "Offer",
                            "price": "0",
                            "priceCurrency": "INR"
                        },
                        "author": {
                            "@type": "Organization",
                            "name": "RoadExpress"
                        }
                    })
                }}
            />
        </section>
    );
};

export default IntroSection;